/**
 * The browser half of an onboarding session: what the BFF cannot know (the URL as typed, the quiz answers,
 * whether the person pressed "Finish onboarding"). The BFF owns the jobs; the transcript is derived from
 * both (see buildTranscript), never stored.
 *
 * Persisted in localStorage under a versioned key, with a TTL. A version mismatch, an expired TTL, a corrupt
 * value or a storage that throws all start a fresh session; none of them ever throws.
 */
import { z } from 'zod';

import { SessionIdSchema } from '@/contracts/onboarding-session';

import { pruneOrphans } from '../quiz/prune';
import type { Answer, Answers } from '../quiz/questions';
import { BRAND_PROFILE_LIMITS } from '../constants';

export const SESSION_STORAGE_KEY = 'onboarding:session:v2';
/** Keys of earlier schemas. They held another operator's data unkeyed, so they are removed, never migrated. */
export const LEGACY_SESSION_STORAGE_KEYS: readonly string[] = ['onboarding:session:v1'];
export const SESSION_SCHEMA_VERSION = 2;
/** How long an untouched session may be resumed. A code constant: the frozen FE env has no room for it. */
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * The brand profile the person confirmed in the chat (phase 3, P3-10): what they sell (the chips left selected,
 * listed ones and custom ones) and the read-only customer, positioning and tone as shown when they confirmed.
 * Browser-only: nothing is sent to the BFF. A text the brand guide could not read is null, never a guess.
 */
export interface BrandProfile {
  products: string[];
  customer: string | null;
  positioning: string | null;
  tone: string | null;
  /** Epoch ms of the confirmation. */
  confirmedAt: number;
}

export interface OnboardingSessionState {
  schemaVersion: typeof SESSION_SCHEMA_VERSION;
  /** Created once with crypto.randomUUID(); the BFF's create-or-attach key. */
  sessionId: string;
  /**
   * Who started this session: the operator's email, lower-cased (what GET /auth/session exposes to the browser;
   * it carries no `sub`). A stored session is resumed only by the same operator. Null when the identity is unknown.
   */
  ownerKey: string | null;
  createdAt: number;
  /** Last change; the TTL counts from here. */
  updatedAt: number;
  /** The website as the person typed it; null until they give one. */
  url: string | null;
  answers: Answers;
  /**
   * The confirmed brand profile; null until the person confirms it. Added after schema 2 shipped, so a stored
   * session without it loads with null (the person confirms again) and no version bump is needed.
   */
  brandProfile: BrandProfile | null;
  /** Set when "Finish onboarding" was pressed. */
  completedAt: number | null;
}

const AnswerSchema = z.object({
  selected: z.array(z.string()),
  text: z.string().optional(),
  skipped: z.boolean().optional(),
});

const profileText = z.string().max(BRAND_PROFILE_LIMITS.text).nullable();
const BrandProfileSchema = z.object({
  products: z.array(z.string().max(BRAND_PROFILE_LIMITS.productLength)).max(BRAND_PROFILE_LIMITS.products),
  customer: profileText,
  positioning: profileText,
  tone: profileText,
  confirmedAt: z.number(),
});

const StoredSessionSchema = z.object({
  schemaVersion: z.literal(SESSION_SCHEMA_VERSION),
  sessionId: SessionIdSchema,
  ownerKey: z.string().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
  url: z.string().nullable(),
  answers: z.record(AnswerSchema),
  // Absent in sessions stored before the profile step, and a malformed one is dropped alone (the person
  // confirms again) rather than discarding the whole session.
  brandProfile: BrandProfileSchema.nullable().optional().catch(null),
  completedAt: z.number().nullable(),
});

export type Clock = () => number;
export type IdFactory = () => string;

/** The key a session is stored under for an operator: their email, trimmed and lower-cased; null when unknown. */
export function normalizeOwnerKey(email: string | null | undefined): string | null {
  const key = email?.trim().toLowerCase();
  return key ? key : null;
}

/** crypto.randomUUID(), with a getRandomValues fallback for older browsers. */
export function newSessionId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function freshSession(
  now: number,
  newId: IdFactory = newSessionId,
  ownerKey: string | null = null,
): OnboardingSessionState {
  return {
    schemaVersion: SESSION_SCHEMA_VERSION,
    sessionId: newId(),
    ownerKey,
    createdAt: now,
    updatedAt: now,
    url: null,
    answers: {},
    brandProfile: null,
    completedAt: null,
  };
}

/** The stored session if it is current and belongs to `ownerKey`, else null. Never throws. */
export function readStoredSession(
  storage: Storage | null,
  now: number,
  ownerKey: string | null = null,
): OnboardingSessionState | null {
  if (!storage) return null;
  let raw: string | null;
  try {
    raw = storage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = StoredSessionSchema.safeParse(parsed);
  // A version mismatch fails the literal and lands here: start fresh.
  if (!result.success) return null;
  if (now - result.data.updatedAt > SESSION_TTL_MS) return null;
  // Another operator on this browser must never see the url and answers of the one before.
  if (result.data.ownerKey !== ownerKey) return null;
  // A session stored before answers were pruned may hold orphans: they must not be resumed.
  return {
    ...result.data,
    answers: pruneOrphans(result.data.answers),
    brandProfile: result.data.brandProfile ?? null,
  } as OnboardingSessionState;
}

/** Writes the session. Returns false when storage refuses (quota, privacy mode); the app carries on in memory. */
export function writeStoredSession(storage: Storage | null, state: OnboardingSessionState): boolean {
  if (!storage) return false;
  try {
    storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export function clearStoredSession(storage: Storage | null): void {
  try {
    storage?.removeItem(SESSION_STORAGE_KEY);
    for (const key of LEGACY_SESSION_STORAGE_KEYS) storage?.removeItem(key);
  } catch {
    // Nothing to clear if storage is unusable.
  }
}

/** This tab's sessionStorage, or null where touching it throws. */
export function browserTabStorage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** localStorage, or null where touching it throws (blocked storage, sandboxed frames). */
export function browserStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/**
 * Resume the stored session of this operator or start a fresh one. A stored session of someone else, or of an
 * older schema, is discarded (and the old keys removed) rather than shown.
 */
export function loadOrCreateSession(
  storage: Storage | null,
  now: number,
  newId: IdFactory = newSessionId,
  ownerKey: string | null = null,
): OnboardingSessionState {
  const resumed = readStoredSession(storage, now, ownerKey);
  if (resumed) return resumed;
  clearStoredSession(storage);
  return freshSession(now, newId, ownerKey);
}

/* -------------------------------- reducer -------------------------------- */

export type SessionAction =
  | { type: 'urlSubmitted'; url: string; now: number }
  /** The BFF refused the address (not a store, not a website): back to asking. */
  | { type: 'urlRejected'; now: number }
  | { type: 'answered'; questionId: string; answer: Answer; now: number }
  /** The person confirmed the brand profile. */
  | { type: 'brandProfileConfirmed'; profile: Omit<BrandProfile, 'confirmedAt'>; now: number }
  | { type: 'completed'; now: number }
  /** A new BFF session id for the same site and answers (the old id is taken by another site). */
  | { type: 'sessionRotated'; sessionId: string; now: number }
  /** Start over under a new session id. */
  | { type: 'reset'; state: OnboardingSessionState };

const clampText = (value: string | null): string | null => {
  const text = value?.trim().slice(0, BRAND_PROFILE_LIMITS.text);
  return text ? text : null;
};

/** What is stored fits the stored schema's bounds, so a long brand-guide value can never make the session unreadable. */
function clampBrandProfile(profile: Omit<BrandProfile, 'confirmedAt'>): Omit<BrandProfile, 'confirmedAt'> {
  return {
    products: profile.products
      .map((p) => p.trim().slice(0, BRAND_PROFILE_LIMITS.productLength))
      .filter((p) => p !== '')
      .slice(0, BRAND_PROFILE_LIMITS.products),
    customer: clampText(profile.customer),
    positioning: clampText(profile.positioning),
    tone: clampText(profile.tone),
  };
}

export function sessionReducer(state: OnboardingSessionState, action: SessionAction): OnboardingSessionState {
  switch (action.type) {
    case 'urlSubmitted':
      return { ...state, url: action.url, updatedAt: action.now };
    case 'urlRejected':
      return { ...state, url: null, updatedAt: action.now };
    case 'answered':
      return {
        ...state,
        // Re-answering an earlier question can drop later ones from the plan: their answers go with them.
        answers: pruneOrphans({ ...state.answers, [action.questionId]: action.answer }),
        updatedAt: action.now,
      };
    case 'brandProfileConfirmed':
      return {
        ...state,
        brandProfile: { ...clampBrandProfile(action.profile), confirmedAt: action.now },
        updatedAt: action.now,
      };
    case 'completed':
      return state.completedAt === null ? { ...state, completedAt: action.now, updatedAt: action.now } : state;
    case 'sessionRotated':
      return { ...state, sessionId: action.sessionId, updatedAt: action.now };
    case 'reset':
      return action.state;
  }
}
